import { useState, useEffect } from 'react';
import {Dialog, DialogTitle, DialogContent, Typography, TextField, DialogActions, Button} from '@mui/material';
import { CheckoutDialogProps } from '../../supplier/currentInventoryReport/data';
import { AssetTypeLabel } from '../../constants/securspace-constants';
import {DialogContentContainer, DialogInputContainer, InputFieldContainer, InputSection, ImageDropContainer} from './styles';
import DropGallery from '../../DropGallery';
import { validateScacCode } from '../../../util/ValidationUtils';

const CheckOutAssetDialog = (props: CheckoutDialogProps) => {
    const {open, asset, onClose, handleCheckout, account, setImageDropZone, snackbarShowMessage, loading} = props;

    const [driverFirstName, setDriverFirstName] = useState<string>('');
    const [driverLastName, setDriverLastName] = useState<string>('');
    const [driverLicenseNumber, setDriverLicenseNumber] = useState<string>('');
    const [truckLicensePlateNumber, setTruckLicensePlateNumber] = useState<string>('');
    const [notes, setNotes] = useState<string>('');
    const [scacCode, setScacCode] = useState<string>('');
    const [scacCodeError, setScacCodeError] = useState<string>('');

    const GALLERY_BUCKET = 'inventory';

    // Reset form state and sync scacCode when asset or dialog open state changes
    useEffect(() => {
        // Start each checkout with a clean form to avoid stale data between assets/opens
        setDriverFirstName('');
        setDriverLastName('');
        setDriverLicenseNumber('');
        setTruckLicensePlateNumber('');
        setNotes('');
        setScacCode(asset?.scacCode || '');
        setScacCodeError('');
    }, [asset, open]);

    const handleSubmit = () => {
        if (asset && driverFirstName && driverLastName && driverLicenseNumber && truckLicensePlateNumber) {
            const trimmedScacCode = scacCode.trim();

            // Validate SCAC code if one is provided
            if (trimmedScacCode && !validateScacCode(trimmedScacCode)) {
                snackbarShowMessage('SCAC code must be alphanumeric and be 2 to 4 characters long.', 'error');
                return;
            }

            const {id, bookingId, locationId, correlationId} = asset;
            const {loggedInUsername} = account;

            const checkOutAsset = {
                id,
                checkOutUser: loggedInUsername,
                bookingId,
                locationId,
                correlationId,
                driverFirstName,
                driverLastName,
                driverLicenseNumber,
                truckLicensePlateNumber,
                notes,
                scacCode: trimmedScacCode
            }
            handleCheckout(checkOutAsset)


        } else {
            if(!driverFirstName) {
                snackbarShowMessage("Please add driver's first name", 'error')
            } else if(!driverLastName) {
                snackbarShowMessage("Please add driver's last name", 'error')
            } else if(!driverLicenseNumber) {
                snackbarShowMessage("Please add driver's license number", 'error')
            } else if(!truckLicensePlateNumber) {
                snackbarShowMessage("Please add truck license plate number", 'error')
            }
        }
    };

    const handleTextFieldChange = (e: {target: {name: string, value: string}}) => {
        const {name, value} = e.target

        switch (name) {
            case 'driverFirstName' :
                setDriverFirstName(value)
                break;
            case 'driverLastName' :
                setDriverLastName(value)
                break;
            case 'driverLicenseNumber' :
                setDriverLicenseNumber(value)
                break;
            case 'truckLicensePlateNumber' :
                setTruckLicensePlateNumber(value)
                break;
            case 'notes' :
                setNotes(value)
                break;
            case 'scacCode' : {
                const uppercaseScacCode = value.toUpperCase();
                setScacCode(uppercaseScacCode);
                setScacCodeError(
                    uppercaseScacCode && !validateScacCode(uppercaseScacCode)
                        ? 'SCAC code must be alphanumeric and be 2 to 4 characters long.'
                        : ''
                );
                break;
        }
        }
    };

    return <Dialog
        open={open}
        onClose={(_, reason) => {
            if (loading && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
            onClose();
        }}
        disableEscapeKeyDown={loading}
        fullWidth
    >
        <DialogTitle variant={'h6'}>Check Out Equipment</DialogTitle>
        <DialogContent>
            <DialogContentContainer>
                <InputFieldContainer>
                    <Typography width='40%'>Asset Type</Typography>
                    <Typography>{asset?.assetType ? AssetTypeLabel[asset?.assetType] : ''}</Typography>
                </InputFieldContainer>
                <InputFieldContainer>
                    <Typography width='70%'>Asset Size</Typography>
                    <Typography>{asset?.assetSize ? `${asset.assetSize}'` : ''}</Typography>
                </InputFieldContainer>
                <InputFieldContainer>
                    <Typography width='70%'>Container Number</Typography>
                    <Typography>{asset?.containerNumber}</Typography>
                </InputFieldContainer>
                <InputFieldContainer>
                    <Typography width='70%'>Chassis Number</Typography>
                    <Typography>{asset?.chassisNumber}</Typography>
                </InputFieldContainer>
                <InputFieldContainer>
                    <Typography width='70%'>Trailer Number</Typography>
                    <Typography>{asset?.trailerNumber}</Typography>
                </InputFieldContainer>
                <InputFieldContainer>
                    <Typography width='70%'>Truck License Plate Number</Typography>
                    <Typography>{asset?.truckLicensePlateNumber}</Typography>
                </InputFieldContainer>
                <InputFieldContainer>
                    <Typography width='70%'>Chassis License Plate Number</Typography>
                    <Typography>{asset?.chassisLicensePlateNumber}</Typography>
                </InputFieldContainer>
                <InputFieldContainer>
                    <Typography width='70%'>SCAC Code</Typography>
                    <TextField
                        name='scacCode'
                        onChange={handleTextFieldChange}
                        value={scacCode}
                        variant='standard'
                        fullWidth
                        placeholder={asset?.scacCode || ''}
                        error={!!scacCodeError}
                        helperText={scacCodeError}
                    />
                </InputFieldContainer>
                <DialogInputContainer>
                    <InputSection>
                        <InputFieldContainer>
                            <Typography width='70%'>First Name</Typography>
                            <TextField
                                name='driverFirstName'
                                onChange={handleTextFieldChange}
                                value={driverFirstName}
                                variant='standard'
                                fullWidth
                                required
                            />
                        </InputFieldContainer>
                        <InputFieldContainer>
                            <Typography width='70%'>Last Name</Typography>
                            <TextField
                                name='driverLastName'
                                onChange={handleTextFieldChange}
                                value={driverLastName}
                                variant='standard'
                                fullWidth
                                required
                            />
                        </InputFieldContainer>
                        <InputFieldContainer>
                            <Typography width='70%'>License Number</Typography>
                            <TextField
                                name='driverLicenseNumber'
                                onChange={handleTextFieldChange}
                                value={driverLicenseNumber}
                                variant='standard'
                                fullWidth
                                required
                            />
                        </InputFieldContainer>
                    </InputSection>
                    <InputFieldContainer>
                        <Typography width='70%'>Truck License Plate Number</Typography>
                        <TextField
                            name='truckLicensePlateNumber'
                            onChange={handleTextFieldChange}
                            value={truckLicensePlateNumber}
                            variant='standard'
                            fullWidth
                            required
                        />
                    </InputFieldContainer>
                    <InputFieldContainer>
                        <Typography width='70%'>Notes</Typography>
                        <TextField
                            name='notes'
                            onChange={handleTextFieldChange}
                            value={notes}
                            variant='standard'
                            fullWidth
                        />
                    </InputFieldContainer>
                    <ImageDropContainer>
                        <DropGallery bucket={GALLERY_BUCKET} updateDropzone={setImageDropZone}/>
                    </ImageDropContainer>
                </DialogInputContainer>
            </DialogContentContainer>
        </DialogContent>
        <DialogActions>
            <Button onClick={handleSubmit} disabled={loading}>{loading ? 'Checking Out...' : 'Complete Check Out'}</Button>
            <Button onClick={onClose} disabled={loading}>Exit</Button>
        </DialogActions>
    </Dialog>
}

export default CheckOutAssetDialog;
